import { Component, Inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ServiceInfoClientService } from '../../../service/service-info-client.service';
import { Observable } from 'rxjs';
import { last, map, startWith } from 'rxjs/operators';
import { CompanyRelations, Owners } from '../../../interface/owners.interface';
import { ActivatedRoute } from '@angular/router';
import { environment } from '../../../../../../../environments/enviroments';

export interface SignatureRequest {
  rut: string;
  nombre: string;
  email: string;
  action: 'select_location' | 'cancel';
}

@Component({
  selector: 'app-request-signature-modal',
  templateUrl: './request-signature-modal.component.html',
  styleUrls: ['./request-signature-modal.component.scss']
})
export class RequestSignatureModalComponent {
  signatureForm: FormGroup;
  owners: Owners[] = [];
  filteredOwners: Observable<Owners[]>;
  companyId: number;
  selectedOwner: Owners | null = null;
  fieldsLocked = false;

  // Directores por defecto
  private readonly defaultDirectors: Owners[] = [
    {
      companyLegalOwnerId: undefined,
      fullRut: '12.231.932-6',
      name: 'Matías',
      lastName: 'Olivos',
      participation: undefined,
      civilState: undefined,
      age: undefined,
      isAval: undefined,
      isRL: true,
      Dicom: undefined,
      deudaSbif: undefined,
      email: 'molivos@inge.cl',
      completeName: 'MATÍAS OLIVOS'
    },
    {
      companyLegalOwnerId: undefined,
      fullRut: '13.831.308-5',
      name: 'Carlos',
      lastName: 'Canales',
      participation: undefined,
      civilState: undefined,
      age: undefined,
      isAval: undefined,
      isRL: true,
      Dicom: undefined,
      deudaSbif: undefined,
      email: 'ccanales@inge.cl',
      completeName: 'CARLOS CANALES'
    }
  ];


  displayFn = (owner: Owners | string | null): string => {
    if (!owner) return '';
    if (typeof owner === 'string') return owner;
    return owner.fullRut || '';
  };

  onFocusRut() {
    // Forzar la actualización del filtro con el valor actual
    const rutControl = this.signatureForm.get('rut');
    if (rutControl) {
      rutControl.updateValueAndValidity();
    }
  }

  clearSelection() {
    this.selectedOwner = null;
    this.fieldsLocked = false;
    this.signatureForm.enable();
    this.signatureForm.reset();
  }

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<RequestSignatureModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private serviceInfo: ServiceInfoClientService,
    private route: ActivatedRoute
  ) {
    this.signatureForm = this.fb.group({
      rut: [null, [Validators.required]],
      nombre: ['', [Validators.required]],
      email: ['', [Validators.required, Validators.email]]
    });

    // Obtener companyId de la URL
    this.route.queryParams.subscribe(params => {
      this.companyId = parseInt(params['client']);
      if (this.companyId) {
        this.loadOwners();
      }
    });

    // Configurar el autocompletado para mostrar todas las opciones al hacer foco
    this.filteredOwners = this.signatureForm.get('rut')!.valueChanges.pipe(
      startWith(''),
      map(value => {
        if (typeof value === 'string') {
          return value ? this._filter(value) : this.owners;
        }
        return this.owners;
      })
    );
  }

  private loadOwners() {
    this.serviceInfo.getCompanyRelations(this.companyId).subscribe(
      (response: CompanyRelations) => {
        // En desarrollo, incluir los RUTs de prueba
        const directors = [...this.defaultDirectors];
        if(this.companyId < 30_000_000) {
          this.serviceInfo.infoCompany(this.companyId).subscribe(
            (response2: any) => {
              const rut = response2?.rut || '';
              const name = response2?.name || '';
              const email = response2?.email || '';
              const completeName = `${name}`;
             const currentClient = 
                [{
                  companyLegalOwnerId: undefined,
                  fullRut: rut,
                  name: name,
                  lastName: '',
                  participation: undefined,
                  civilState: undefined,
                  age: undefined,
                  isAval: undefined,
                  isRL: true,
                  Dicom: undefined,
                  deudaSbif: undefined,
                  email: email,
                  completeName: completeName
                }];
              this.owners = [
                ...directors,
                ...currentClient,
                ...(response?.legalOwners || [])
              ];
            }
            )
          }
          else{
            // Combinar los directores con los propietarios legales
            this.owners = [
              ...directors,
              ...(response?.legalOwners || [])
            ];
        }
      },
      (error: any) => {
        console.error('Error al obtener los propietarios:', error);
        // En caso de error, mostrar los directores por defecto y en desarrollo los de prueba
        const directors = [...this.defaultDirectors];
        
        this.owners = directors;
      }
    );
  }

  private _filter(value: string): Owners[] {
    const filterValue = value.toLowerCase();
    return this.owners.filter(owner => {
      const rutMatch = owner.fullRut?.toString().toLowerCase().includes(filterValue);
      const nameMatch = owner.completeName?.toLowerCase().includes(filterValue);
      return rutMatch || nameMatch;
    });
  }

  onOwnerSelected(event: any) {
    this.selectedOwner = event.option.value as Owners;
    if (this.selectedOwner) {
      this.signatureForm.patchValue({
        rut: this.selectedOwner.fullRut || '',
        nombre: this.selectedOwner.completeName || '',
        email: this.selectedOwner.email || ''
      });
      
      // Bloquear los campos nombre y email
      this.fieldsLocked = true;
      this.signatureForm.get('nombre')?.disable();
      this.signatureForm.get('email')?.disable();
    } else {
      this.clearSelection();
    }
  }

  onSubmit() {
    if (this.signatureForm.valid) {
      const formValue = {
        rut: this.signatureForm.get('rut')?.value,
        nombre: this.signatureForm.get('nombre')?.value,
        email: this.signatureForm.get('email')?.value
      };

      const response: SignatureRequest = {
        ...formValue,
        action: 'select_location'
      };
      this.dialogRef.close(response);
    }
  }

  closeDialog() {
    const response: SignatureRequest = {
      rut: '',
      nombre: '',
      email: '',
      action: 'cancel'
    };
    this.dialogRef.close(response);
  }
}
